import type { Money } from './types';

/**
 * Dashboard numbers.
 *
 * A dashboard is read at a glance, so it drops what a glance does not need:
 * cents on a six-figure total, and digits past three on an axis. The exact
 * amount is never lost — it is in the title of every figure and on the page
 * each card opens.
 */

/** A decimal string as a number, for drawing only. Never for arithmetic on money. */
export function toNumber(value: Money): number {
  if (value === null || value === '') return 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/** "AED 114,450" — whole units, grouped. */
export function wholeMoney(value: Money, currency: string): string {
  if (value === null) return '—';
  const rounded = Math.round(toNumber(value));
  return `${currency} ${rounded.toLocaleString('en-US')}`;
}

/** "AED 114.5K", "AED 2.1M" — for tiles and axis ticks where width is short. */
export function compactMoney(value: Money | number, currency?: string): string {
  const amount = typeof value === 'number' ? value : toNumber(value);
  const text = new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: Math.abs(amount) >= 1000 ? 1 : 0,
  }).format(amount);
  return currency ? `${currency} ${text}` : text;
}

/** "AED 114,450.00" — the exact amount, for titles and tooltips. */
export function exactMoney(value: Money, currency: string): string {
  if (value === null) return '—';
  const [whole = '0', fraction = '00'] = value.replace('-', '').split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${currency} ${value.startsWith('-') ? '-' : ''}${grouped}.${fraction.padEnd(2, '0').slice(0, 2)}`;
}

/** Adds decimal strings in minor units, so a year of cents does not drift. */
export function sumMoney(values: Money[]): string {
  const minor = values.reduce((total, value) => {
    const text = value ?? '0';
    const negative = text.startsWith('-');
    const [whole = '0', cents = ''] = text.replace('-', '').split('.');
    const units = BigInt(whole || '0') * 100n + BigInt((cents + '00').slice(0, 2));
    return total + (negative ? -units : units);
  }, 0n);
  const negative = minor < 0n;
  const abs = negative ? -minor : minor;
  return `${negative ? '-' : ''}${abs / 100n}.${(abs % 100n).toString().padStart(2, '0')}`;
}

/** "+12%" against a comparison, or null when there is nothing to compare with. */
export function percentChange(current: number, previous: number): number | null {
  if (previous === 0) return current === 0 ? 0 : null;
  return Math.round(((current - previous) / previous) * 100);
}
